import { useScopedClient } from "@/features/clients/hooks/use-scoped-client";
import { useCustomerSummary } from "@/features/customers/hooks/use-customer-summary";
import {
  customerDisplayName,
  isNameUnsure,
} from "@/features/customers/name-verification";
import { UnsureNameMark } from "@/components/molecules/unsure-name-mark";
import { lastActivityAt, type SessionListItem } from "@/features/sessions";
import {
  classificationColor,
  shownTags,
} from "@/features/sessions/classification";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { ClockIcon, RobotIcon, TagIcon } from "@phosphor-icons/react";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { format } from "date-fns";
import {
  MetaIcon,
  ResultBadge,
  formatDuration,
  useChannel,
  useTagLabel,
} from "./conversation-meta";

/**
 * One row of the conversation list — the equivalent of a mail client's message
 * row: who it is with, what it was about, what was said, and everything else as
 * icons. Nine table columns do not fit in 380px of width, and the three that
 * matter (customer, subject, summary) are the ones a human scans for, with the
 * outcome as a coloured pill beside them; the rest (channel, duration, agent,
 * classification) keep their information as glyphs with tooltips.
 */
export function ConversationListItem({
  session,
  selected,
  onSelect,
}: {
  session: SessionListItem;
  selected: boolean;
  onSelect: (session: SessionListItem) => void;
}) {
  const { t } = useTranslation("calls");
  const { clientId } = useScopedClient();
  const { data: customer } = useCustomerSummary(clientId, session.customer_id);
  const { data: agents = [] } = useAgents();
  const channel = useChannel(session);
  const tagLabel = useTagLabel();
  const tags = shownTags(session.tags);

  const customerName =
    customerDisplayName(customer) ||
    session.context?.phone_number ||
    session.context?.email ||
    t("list.unknownCustomer");

  // The subject line, and the one-paragraph summary under it. An outbound call
  // carries its purpose instead of a summary until it has one: that purpose is
  // why the call was placed, which is the same question the summary answers.
  const title = session.name || t("list.untitled");
  const description =
    session.summary ||
    (session.type === "outbound" ? session.purpose : "") ||
    "";

  const agentName = agents.find((a) => a.id === session.agent_id)?.name;
  const locale = useDateFnsLocale();
  // The hour only: the day is the header the list groups this row under
  // (`conversation-list.tsx`), read once for every conversation of that day.
  const when = format(lastActivityAt(session), "p", { locale });

  return (
    <button
      type="button"
      onClick={() => onSelect(session)}
      aria-current={selected}
      className={cn(
        "w-full border-b border-l-2 px-3 py-3 text-left transition-colors",
        "hover:bg-slate-50 dark:hover:bg-slate-900",
        selected
          ? "border-l-primary bg-slate-100 dark:bg-slate-900"
          : "border-l-transparent",
      )}
    >
      <div className="flex items-center gap-2">
        <channel.Icon
          className="h-4 w-4 shrink-0"
          weight="fill"
          style={{ color: channel.color }}
        />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">
          {customerName}
          {isNameUnsure(customer) && <UnsureNameMark />}
        </span>
        {session.state === "open" && (
          <span className="size-2 shrink-0 animate-pulse rounded-full bg-orange-400" />
        )}
        <span className="shrink-0 text-xs text-muted-foreground">{when}</span>
      </div>

      <div className="mt-1 truncate text-sm">{title}</div>

      {description && (
        <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
          {description}
        </div>
      )}

      <div className="mt-2 flex items-center gap-3 overflow-hidden">
        <ResultBadge
          session={session}
          className="shrink-0 px-1.5 py-0 text-[11px] font-medium"
        />
        {session.duration > 0 && (
          <MetaIcon
            icon={ClockIcon}
            label={t("table.duration")}
            value={formatDuration(session.duration / 1000)}
          />
        )}
        {tags.length > 0 && (
          <MetaIcon
            icon={TagIcon}
            label={t("table.classification")}
            value={
              tags.length > 1
                ? `${tagLabel(tags[0])} +${tags.length - 1}`
                : tagLabel(tags[0])
            }
            color={classificationColor(tags[0])}
            className="min-w-0 flex-1"
          />
        )}
        {agentName && (
          <MetaIcon
            icon={RobotIcon}
            label={t("table.agent")}
            value={agentName}
            className="ml-auto max-w-[45%] shrink"
          />
        )}
      </div>
    </button>
  );
}
